import { useEffect, useState } from 'react';

/**
 * Custom hook that calculates the dynamic height for the main content area based on the window height and banner height.
 *
 * @returns {number | string} The dynamic height of the main content area, either as a number representing the height in pixels or a string representing the height in viewport units (e.g., '100vh').
 *
 * This hook depends on `useState` and `useEffect` from React, as well as the global `window` and `document` objects.
 *
 * Example usage:
 *
 * ```jsx
 * import React from 'react';
 * import { useCalculateMainContentDynamicHeight } from './path-to-your-hook';
 *
 * const MyComponent = () => {
 *   const mainContentHeight = useCalculateMainContentDynamicHeight();
 *
 *   return (
 *     <div style={{ height: mainContentHeight }}>
 *       // Your content here
 *     </div>
 *   );
 * };
 *
 * export default MyComponent;
 * ```
 */
export const useCalculateMainContentDynamicHeight = (): number | string => {
  const minimalHeight = '100vh';
  const [dynamicHeight, setDynamicHeight] = useState<number | string>(minimalHeight);

  function getBannerHeight() {
    const bannerHeight = document.querySelector('body div[role="banner"]')?.clientHeight;
    if (bannerHeight && bannerHeight > 0) {
      return bannerHeight;
    }
    return 0;
  }

  const calculateDynamicHeight = () => {
    const bannerHeight = getBannerHeight();
    const windowHeight = window?.outerHeight;
    if (windowHeight) {
      if (windowHeight > bannerHeight) {
        return windowHeight - bannerHeight;
      }
      return windowHeight;
    }
    return minimalHeight;
  };

  useEffect(() => {
    setDynamicHeight(calculateDynamicHeight());

    const handleResize = () => {
      setDynamicHeight(calculateDynamicHeight());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return dynamicHeight;
};
